import React, { useEffect, useState } from "react";
import { api, fmtMoney, STAGES } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import YearEndReport from "../components/YearEndReport";

const STAGE_COLORS = {
  Prospect: "#d1cece", CMA: "#e1b7c1", Listed: "#e1b7c1",
  Contract: "#bf858e", Closing: "#bf858e", Closed: "#050303",
};

export default function Pipeline() {
  const [deals, setDeals] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);  // deal object when editing, null otherwise

  const load = () => api.get("/deals").then(r => setDeals(r.data || []));
  useEffect(() => { load(); }, []);

  const onDragStart = (e, id) => e.dataTransfer.setData("id", id);
  const onDrop = async (e, stage) => {
    const id = e.dataTransfer.getData("id");
    if (!id) return;
    setDeals(d => d.map(x => x.id === id ? { ...x, stage } : x));
    try {
      await api.patch(`/deals/${id}/stage`, { stage });
      toast.success(`Moved to ${stage}`);
    } catch { toast.error("Move failed"); load(); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this deal?")) return;
    await api.delete(`/deals/${id}`);
    load();
  };

  return (
    <div className="space-y-7" data-testid="pipeline-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Command Center</div>
          <h1 className="font-heading text-5xl mt-2">Deal Pipeline</h1>
          <p className="text-sm text-[#050303]/60 mt-2">Drag cards across stages — click the pencil on any card to edit price, dates, or stage.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <YearEndReport />
          <NewDealDialog open={open} setOpen={setOpen} onCreate={load} />
        </div>
      </header>

      <div className="overflow-x-auto -mx-2 px-2">
        <div className="flex gap-4 min-w-max pb-4">
          {STAGES.map((stage) => {
            const stageDeals = deals.filter(d => d.stage === stage);
            const total = stageDeals.reduce((s, d) => s + d.price, 0);
            return (
              <div
                key={stage}
                className="w-72 shrink-0"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDrop(e, stage)}
                data-testid={`pipeline-column-${stage}`}
              >
                <div className="mb-3 px-1 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: STAGE_COLORS[stage] }} />
                    <span className="overline">{stage}</span>
                    <span className="text-xs text-[#050303]/40">· {stageDeals.length}</span>
                  </div>
                  <span className="text-xs font-semibold text-[#050303]/60">{fmtMoney(total)}</span>
                </div>
                <div className="space-y-3 min-h-[400px] p-2 rounded-xl bg-[#ffffff] border border-[#d1cece]">
                  {stageDeals.map(d => (
                    <div
                      key={d.id}
                      draggable
                      onDragStart={(e) => onDragStart(e, d.id)}
                      className="core-card-flat p-4 cursor-grab active:cursor-grabbing hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
                      style={{ borderLeft: `3px solid ${STAGE_COLORS[stage]}` }}
                      data-testid={`deal-card-${d.id}`}
                    >
                      {d.image_url && <img src={d.image_url} alt="" className="w-full h-28 object-cover rounded-md mb-3" />}
                      <div className="font-semibold text-sm leading-tight">{d.title}</div>
                      <div className="text-xs text-[#050303]/55 mt-1">{d.address}</div>
                      <div className="flex items-center justify-between mt-3">
                        <span className="font-heading text-lg">{fmtMoney(d.price)}</span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setEditing(d)}
                            className="text-[#050303]/40 hover:text-[#bf858e]"
                            title="Edit deal"
                            data-testid={`edit-deal-${d.id}`}
                          >
                            <Pencil size={14} strokeWidth={1.5} />
                          </button>
                          <button
                            onClick={() => del(d.id)}
                            className="text-[#050303]/40 hover:text-[#bf858e]"
                            title="Delete deal"
                            data-testid={`delete-deal-${d.id}`}
                          >
                            <Trash2 size={14} strokeWidth={1.5} />
                          </button>
                        </div>
                      </div>
                      <div className="text-[11px] text-[#050303]/50 mt-2">{d.client}</div>
                      {d.close_date && (
                        <div className="text-[10px] text-[#bf858e] mt-1" data-testid={`deal-close-date-${d.id}`}>
                          Closes {d.close_date}
                        </div>
                      )}
                    </div>
                  ))}
                  {stageDeals.length === 0 && <div className="text-center text-xs text-[#050303]/30 py-6">No deals</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editing && (
        <EditDealDialog
          deal={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function NewDealDialog({ open, setOpen, onCreate }) {
  const [form, setForm] = useState({
    title: "", address: "", client: "", price: "", commission_pct: 3.0,
    stage: "Prospect", close_date: "", notes: "", image_url: "",
  });
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Use the long-lived signed URL returned by the backend (365d TTL)
      // so deal photos survive login sessions and can be viewed from any browser.
      const url = data.url.startsWith("http")
        ? data.url
        : `${window.location.protocol}//${window.location.host}${data.url}`;
      setForm(f => ({ ...f, image_url: url }));
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/deals", {
        ...form,
        price: parseFloat(form.price),
        commission_pct: parseFloat(form.commission_pct),
      });
      toast.success("Deal added");
      setOpen(false);
      setForm({
        title: "", address: "", client: "", price: "", commission_pct: 3.0,
        stage: "Prospect", close_date: "", notes: "", image_url: "",
      });
      onCreate();
    } catch { toast.error("Create failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="bg-[#050303] text-white pill-btn hover:bg-[#bf858e] flex items-center gap-2" data-testid="new-deal-btn">
          <Plus size={16} /> New Deal
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-2xl">New Deal</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="new-deal-form">
          <div><Label>Title</Label><Input data-testid="deal-title" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
          <div><Label>Address</Label><Input data-testid="deal-address" required value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
          <div><Label>Client</Label><Input data-testid="deal-client" required value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Sales Price</Label><Input data-testid="deal-price" type="number" required value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} /></div>
            <div><Label>Commission %</Label><Input type="number" step="0.1" value={form.commission_pct} onChange={e => setForm({ ...form, commission_pct: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger data-testid="deal-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Close Date</Label>
              <Input type="date" data-testid="deal-close-date" value={form.close_date} onChange={e => setForm({ ...form, close_date: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea data-testid="deal-notes" rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div>
            <Label>Listing Photo</Label>
            <input type="file" accept="image/*" onChange={uploadPhoto} className="block w-full text-sm mt-1" data-testid="deal-photo" />
            {uploading && <div className="text-xs text-[#050303]/60 mt-1">Uploading…</div>}
            {form.image_url && <img src={form.image_url} alt="" className="mt-2 w-full h-24 object-cover rounded-md" />}
          </div>
          <button type="submit" className="w-full bg-[#050303] text-white pill-btn hover:bg-[#bf858e]" data-testid="submit-new-deal">Create Deal</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function EditDealDialog({ deal, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: deal.title || "",
    address: deal.address || "",
    client: deal.client || "",
    price: String(deal.price ?? ""),
    commission_pct: String(deal.commission_pct ?? 3.0),
    stage: deal.stage || "Prospect",
    close_date: deal.close_date || "",
    notes: deal.notes || "",
    image_url: deal.image_url || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url.startsWith("http")
        ? data.url
        : `${window.location.protocol}//${window.location.host}${data.url}`;
      setForm(f => ({ ...f, image_url: url }));
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/deals/${deal.id}`, {
        ...form,
        price: parseFloat(form.price) || 0,
        commission_pct: parseFloat(form.commission_pct) || 0,
      });
      toast.success("Deal updated");
      onSaved();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" data-testid="edit-deal-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Edit Deal</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="edit-deal-form">
          <div><Label>Title</Label><Input data-testid="edit-deal-title" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
          <div><Label>Address</Label><Input data-testid="edit-deal-address" required value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
          <div><Label>Client</Label><Input data-testid="edit-deal-client" required value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Sales Price</Label><Input data-testid="edit-deal-price" type="number" required value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} /></div>
            <div><Label>Commission %</Label><Input type="number" step="0.1" value={form.commission_pct} onChange={e => setForm({ ...form, commission_pct: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger data-testid="edit-deal-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Close Date</Label>
              <Input type="date" data-testid="edit-deal-close-date" value={form.close_date} onChange={e => setForm({ ...form, close_date: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea data-testid="edit-deal-notes" rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div>
            <Label>Listing Photo</Label>
            <input type="file" accept="image/*" onChange={uploadPhoto} className="block w-full text-sm mt-1" data-testid="edit-deal-photo" />
            {uploading && <div className="text-xs text-[#050303]/60 mt-1">Uploading…</div>}
            {form.image_url && <img src={form.image_url} alt="" className="mt-2 w-full h-24 object-cover rounded-md" />}
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="border border-[#050303] text-[#050303] pill-btn hover:bg-[#050303] hover:text-white"
              data-testid="edit-deal-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-[#050303] text-white pill-btn hover:bg-[#bf858e] flex-1 disabled:opacity-60"
              data-testid="edit-deal-save"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
